// CareLogix™ ORACLE — OraclePatternScanner (Phase 33 §2)
// Animated pattern detection grid using deterministic seeded scores.
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PALETTE, scoreColor } from "@/components/animation/MotionShared";
import { predictiveApi } from "@/api/phase33Client";

const PATTERNS = [
  { id: "bull_flag",      label: "Bull Flag",        bias: "buy"   },
  { id: "head_shoulders", label: "Head & Shoulders", bias: "avoid" },
  { id: "cup_handle",     label: "Cup & Handle",     bias: "buy"   },
  { id: "double_top",     label: "Double Top",       bias: "avoid" },
  { id: "double_bottom",  label: "Double Bottom",    bias: "buy"   },
  { id: "triangle_asc",   label: "Asc. Triangle",    bias: "buy"   },
  { id: "triangle_desc",  label: "Desc. Triangle",   bias: "avoid" },
  { id: "wedge_rising",   label: "Rising Wedge",     bias: "watch" },
  { id: "flag_bear",      label: "Bear Flag",        bias: "avoid" },
  { id: "breakout_box",   label: "Breakout Box",     bias: "buy"   },
  { id: "reversal_v",     label: "V Reversal",       bias: "watch" },
  { id: "consolidation",  label: "Consolidation",    bias: "hold"  },
];

function seedScore(symbol, domain, id) {
  let h = 0;
  const s = `${symbol}-${domain}-${id}`;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 100;
}

export default function OraclePatternScanner({ symbols, domain = "stocks", testid = "oracle-pattern-scanner" }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const list = (symbols && symbols.length ? symbols : ["AAPL", "MSFT", "NVDA", "GOOGL", "AMZN", "META"]);
  const [cards, setCards] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    predictiveApi.cards(list, domain).then((r) => { if (!cancelled) setCards(r.data?.cards || null); }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.join(","), domain]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const grid = useMemo(() => {
    return list.map((sym, i) => {
      const detections = PATTERNS
        .map((p) => ({ ...p, score: seedScore(sym, domain, p.id) }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 4);
      const ai = cards?.[i]?.scores?.ai_rank;
      return { symbol: sym, detections, aiRank: ai ?? seedScore(sym, domain, "airank") };
    });
  }, [list, domain, cards]);

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <div style={S.eyebrow}>ORACLE PATTERN SCANNER™</div>
          <h3 style={S.title}>Pattern Detection · {domain.toUpperCase()}</h3>
        </div>
      </header>
      <div style={S.grid}>
        {grid.map((row) => (
          <motion.article key={row.symbol} style={S.card} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} data-testid={`${testid}-card-${row.symbol}`}>
            <header style={S.cardHead}>
              <h4 style={S.cardTitle}>{row.symbol}</h4>
              <span style={{ ...S.aiPill, color: scoreColor(row.aiRank), borderColor: scoreColor(row.aiRank) }}>AI {row.aiRank}</span>
            </header>
            <ul style={S.detList}>
              {row.detections.map((d) => (
                <li key={d.id} style={S.detRow}>
                  <span style={S.detLabel}>{d.label}</span>
                  <span style={S.detBarWrap}>
                    <motion.span style={{ ...S.detBarFill, background: scoreColor(d.score) }} initial={{ width: 0 }} animate={{ width: `${d.score}%` }} transition={{ duration: 0.8 }} />
                  </span>
                  <span style={{ ...S.detScore, color: scoreColor(d.score) }}>{d.score}</span>
                </li>
              ))}
            </ul>
          </motion.article>
        ))}
      </div>
      <footer style={S.foot}>Pattern detection is decision-support only · HITL required.</footer>
    </section>
  );
}

const S = {
  shell:    { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, padding: 16, color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 12, minWidth: 0 },
  head:     { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" },
  eyebrow:  { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.20em" },
  title:    { margin: 0, color: PALETTE.goldBright, fontSize: 16 },
  grid:     { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 },
  card:     { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", gap: 8 },
  cardHead: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  cardTitle:{ margin: 0, color: PALETTE.goldBright, fontSize: 14 },
  aiPill:   { fontFamily: "'Space Mono', monospace", fontSize: 10, padding: "2px 7px", border: "1px solid", borderRadius: 999 },
  detList:  { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 },
  detRow:   { display: "grid", gridTemplateColumns: "1fr 80px 36px", alignItems: "center", gap: 6 },
  detLabel: { color: PALETTE.slate3, fontSize: 11.5 },
  detBarWrap:{ height: 5, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" },
  detBarFill:{ height: "100%", width: 0, display: "block" },
  detScore: { fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, textAlign: "right" },
  foot:     { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5, textAlign: "right" },
};
